import {AfterViewInit, Component, NgZone, OnInit, QueryList, ViewChild, ViewChildren} from "@angular/core";
import {ActionsResource} from "../repository/actions-resource.service";
import {AssignBoardFilters} from "./assign-board-filters/assign-board-filters";
import {logAsync} from "../../../../helpers/logging";
import {AssignBoardControlsComponent} from "./assign-board-controls/assign-board-controls.component";
import {catchError, concatMap, filter, map, startWith, switchMap, tap} from "rxjs/operators";
import {Localization} from "../../../common/enums/localization";
import {ActionsQuery} from "../repository/actions-query";
import {Destroyable, takeUntilDestroyed} from "../../../../angular/component/destruction/destroyable.interface";
import {forkJoin, Subject, throwError} from "rxjs";
import {SelfDestruct} from "../../../../angular/component/destruction/self-destruct";
import {ActionStatusType} from "../../../common/enums/enums";
import {ActionSubTitle, DraggableActionComponent} from "../draggable-action/draggable-action.component";
import {TranslateService} from "@ngx-translate/core";
import {InterventionTeamService} from "../../../buildings/service/inspection-team.service";
import {LoadsData, trackLoadingOn} from "../../../../angular/component/loading/loads-data";
import {InterventionTeamSummary} from "../../../buildings/model/Intervention-team-summary";
import {SnackBarService} from "../../../../services/snackbar.service";
import {ConfirmationDialogModel, DialogService} from "@npm.chapps.com/chapps.common.angular.library";
import {CdkDrag, CdkDragDrop} from "@angular/cdk/drag-drop";
import {Action} from "../../../common/model/action";
import {JobModalComponent, JobModalData} from "../../../buildings/issues/components/job-modal/job-modal.component";
import {MatDialog} from '@angular/material/dialog';
import {InterventionTeamDetailDto} from '../../intervention-teams/teams/intervention-team-detail.dto';
import {Activity} from '../../../../helpers/activity';

@Component({
  selector: 'app-action-assign-board',
  templateUrl: './action-assign-board.component.html',
  styleUrls: ['./action-assign-board.component.scss'],
})
export class ActionAssignBoardComponent implements OnInit, AfterViewInit, Destroyable, LoadsData {
  public readonly destroyed$ = new Subject<ActionAssignBoardComponent>();
  public readonly Localization = Localization;
  public filters: AssignBoardFilters;
  public loading = false;
  public actions: Action[];
  public teams: InterventionTeamDetailDto[];
  protected selectedTeam: InterventionTeamDetailDto;
  @ViewChildren(DraggableActionComponent)
  protected actionComponents: QueryList<DraggableActionComponent>;
  protected readonly ActionSubTitle = ActionSubTitle;
  protected readonly activity: Activity<AssignBoardActivity> = new Activity(AssignBoardActivity);
  protected selectedTeamActions: Action[];
  @ViewChild(AssignBoardControlsComponent)
  private readonly filtersComponent: AssignBoardControlsComponent;

  constructor(
    private readonly actionsResource: ActionsResource,
    private readonly translate: TranslateService,
    private readonly teamService: InterventionTeamService,
    private readonly snackbar: SnackBarService,
    private readonly dialogs: DialogService,
    private readonly dialog: MatDialog,
    private readonly zone: NgZone,
  ) {
  }

  public get selectedActions(): Action[] {
    return this.actionComponents
      ?.filter(actionComponent => actionComponent.selected)
      ?.map(actionComponent => actionComponent.model);
  }

  public get hasActionsSelected(): boolean {
    return this.selectedActions?.length > 0;
  }

  public get unassignedActions() {
    return this.actions?.filter(action => !action.interventionTeam && !action.interventionTeamId);
  }

  protected get hasUnsentActions() {
    return this.teams?.some(
      team => team.numberOfUnsentActions > 0,
    );
  }

  protected get isLoadingDetail() {
    return this.activity.isBusyWith(AssignBoardActivity.LoadSelectedTeamActions);
  }

  protected get isSendingActions() {
    return this.activity.isBusyWith(AssignBoardActivity.SendActions);
  }

  protected get totalUnsentActions() {
    return this.teams
      .map(team => team.numberOfUnsentActions)
      .reduce((total, teamCount) => total + teamCount, 0);
  }

  private get teamsWithUnsentActions() {
    return this.teams.filter(({numberOfUnsentActions}) => numberOfUnsentActions > 0);
  }

  ngOnInit() {
    this.fetchTeams();
  }

  public ngAfterViewInit() {
    this.filtersComponent.filtersChange.asObservable().pipe(
      startWith(new AssignBoardFilters()),
      filter(filters => !!filters),
      logAsync('Filters changed'),
      switchMap(filters => this.fetchActionsUsing(filters)),
      logAsync('Actions received'),
      takeUntilDestroyed(this),
    ).subscribe(actions => this.actions = actions.items);
  }

  public ngOnDestroy() {
    SelfDestruct(this);
  }

  public findActionFor(team: InterventionTeamSummary): Action[] {
    return this.actions?.filter(action => action.interventionTeamId === team.id);
  }

  public localisedStatusFor(action: Action) {
    switch (action.status) {
      case ActionStatusType.OPEN:
        return !action.interventionTeamId ?
          this.translate.instant(Localization.Unassigned) : this.translate.instant(Localization.Requested) + ' | ' + this.translate.instant(Localization.ToSync);
      case ActionStatusType.SENT:
        return this.translate.instant(Localization.Requested);
      case ActionStatusType.EXECUTING:
        return this.translate.instant(`actionStatusType_${ActionStatusType.ACCEPTED}`) + ' | ' + this.translate.instant(`actionStatusType_${ActionStatusType.EXECUTING}`);
    }
    return this.translate.instant(`actionStatusType_${action.status}`);
  }

  public assignActionToTeam([action, team]: [Action, InterventionTeamDetailDto]) {
    trackLoadingOn(
      this.actionsResource.assign(action.id, team.id), this, 'loading',
    ).subscribe(() => this.didAssignAction(action, team));
  }

  public deleteSingle(action: Action) {
    this.dialogs.confirm(ConfirmationDialogModel.create({
      title: this.translate.instant(Localization.Delete) + ' ' + this.translate.instant(`actionType_${action.type}`),
      text: this.translate.instant(Localization.AreYouSureYouWantToDeleteThisJob),
      confirmText: Localization.Delete,
      type: 'danger',
    } as ConfirmationDialogModel))
      .pipe(
        filter(Boolean),
        switchMap(() => this.delete(action)),
      ).subscribe();
  }

  public deleteSelectedActions() {
    this.dialogs.confirm(ConfirmationDialogModel.create({
      title: `Delete ${this.selectedActions.length} actions?`,
      text: `Are you sure you wish to delete ${this.selectedActions.length} action(s)? This cannot be undone.`,
      confirmText: Localization.Delete,
      type: 'danger',
    } as ConfirmationDialogModel)).pipe(
      filter(Boolean),
      concatMap(() => {
        return forkJoin(this.selectedActions.map(action => this.delete(action)))
      }),
    ).subscribe()
  }

  canUnassign(draggedAction: CdkDrag<Action>) {
    return draggedAction.data.status === ActionStatusType.OPEN && !!draggedAction.data.interventionTeamId;
  }

  unassign(droppedAction: CdkDragDrop<unknown, unknown, Action>) {
    const action = droppedAction.item.data;
    const oldTeam = action.interventionTeam;

    action.interventionTeam = action.interventionTeamId = null;

    this.actionsResource.unassign(action)
      .subscribe(
        () => {
        },
        () => {
          action.interventionTeam = oldTeam;
          action.interventionTeamId = oldTeam.id;
        },
      );
  }

  public assignSelectedActionsTo(team: InterventionTeamDetailDto) {
    forkJoin(
      this.selectedActions.map(action => {
        action.interventionTeamId = team.id;
        return this.actionsResource.assign(action.id, team.id).pipe(
          catchError((error) => {
            action.interventionTeamId = null;
            return throwError(error);
          }),
        );
      }),
    ).subscribe()

  }

  public openJobDialog(action: Action) {
    const data = {issueGuid: action.issues[0].issueGuid, action} as JobModalData;
    const dialog = this.dialog.open(JobModalComponent, {panelClass: 'full-bleed', data});
    dialog.componentInstance.actionAdded$.subscribe(persistedAction => {
      Object.assign(action, persistedAction);
    });
  }

  public async select(team: InterventionTeamDetailDto) {
    this.selectedTeam = team;
    await this.fetchActionsAssignedTo(team);
  }

  public isSelected(team: InterventionTeamDetailDto) {
    return this.selectedTeam === team;
  }

  public didDrop($event: CdkDragDrop<unknown, unknown, Action>, team: InterventionTeamDetailDto) {
    this.assignActionToTeam([$event.item.data, team]);
  }

  public async sendAssignedActions() {
    const sendActionsRequests = this.teamsWithUnsentActions.map(
      team => this.actionsResource.sendAssignedActionsOf(team).toPromise(),
    );

    await this.activity
      .track(AssignBoardActivity.SendActions)
      .operation(async () => await Promise.all(sendActionsRequests));

    this.teams.forEach(team => team.numberOfUnsentActions = 0);
  }

  private fetchTeams() {
    const teamsRequest = this.teamService.getAll().pipe(
      map(teamsResponse => teamsResponse.items),
      takeUntilDestroyed(this),
    );
    trackLoadingOn(teamsRequest, this, 'loading')
      .subscribe(teams => this.teams = teams);
  }

  private fetchActionsUsing(filters: AssignBoardFilters) {
    const actionsRequest = this.actionsResource.all(ActionsQuery.with({
      buildingGuids: [filters.buildingGuid],
      type: filters.jobType,
      statuses: [ActionStatusType.OPEN, ActionStatusType.SENT, ActionStatusType.ACCEPTED],
    }));
    return trackLoadingOn(actionsRequest, this, 'loading');
  }

  private didAssignAction(action: Action, team: InterventionTeamDetailDto) {
    const teamName = team.label;
    const type = this.translate.instant(`actionType_${action.type}`);
    this.snackbar.show(
      this.translate.instant(Localization.ActionAssignedNotification, {type, teamName}),
    );
    action.interventionTeamId = team.id;
    team.numberOfActiveActions++;
    team.numberOfUnsentActions++;
    if (team.id === this.selectedTeam?.id) {
      this.selectedTeamActions.push(action);
    }
  }

  private didDeleteAction(action: Action) {
    const type = this.translate.instant(`actionType_${action.type}`);
    this.snackbar.show(this.translate.instant(Localization.ActionDeletedNotification, {type}));
    this.actions = this.actions.filter(({id}) => id !== action.id);
  }

  private delete(action: Action) {
    return trackLoadingOn(this.actionsResource.delete(action), this, 'loading').pipe(
      tap(() => this.didDeleteAction(action)),
    );
  }

  private async fetchActionsAssignedTo(team: InterventionTeamDetailDto) {
    const actionsRequest = this.actionsResource.all(ActionsQuery.with({
      interventionTeams: [team.id],
      statuses: [ActionStatusType.OPEN, ActionStatusType.SENT, ActionStatusType.ACCEPTED, ActionStatusType.EXECUTING],
    })).toPromise();
    const actionsResponse = await this.activity.track(AssignBoardActivity.LoadSelectedTeamActions)
      .operation(async () => await actionsRequest);

    this.selectedTeamActions = actionsResponse.items;
  }

}

enum AssignBoardActivity {
  LoadUnassignedActions,
  LoadInterventionTeams,
  LoadSelectedTeamActions,
  SendActions,
}
